/* eslint-disable react/sort-comp */
/**
 * Essentially a <input type="text"> with a text label above, with an optional
 * error message (which causes the field to turn red). When type="password" is
 * specified, turns into a password field.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";

// A counter to generate unique IDs from.
let uniqueIdCounter = 0;

const warningSign = {
    path: "M57.379 77.077l0-10.633q0-.784-.539-1.323t-1.225-.539l-10.78 0q-.784 0-1.274.539t-.49 1.323l0 10.633q0 .784.539 1.323t1.225.539l10.78 0q.784 0 1.274-.539t.49-1.323zm-2.009-19.649q1.813 0 1.911-1.274l1.029-25.725q.049-.686-.637-1.176t-1.274-.49l-12.348 0q-.686 0-1.274.49t-.637 1.274l.98 25.627q0 1.176 1.911 1.274l10.339 0zm37.877 35.819l-86.044 0q-4.165 0-6.174-3.528t-.098-7.056l43.022-78.841q.98-1.715 2.646-2.744 3.577-2.156 7.252 0 1.715 1.029 2.646 2.744l43.022 78.841q1.96 3.528-.147 7.056-.931 1.568-2.597 2.548t-3.528.98z",
    width: 100,
    height: 92.816,
} as const;

type FieldType = "text" | "password" | "email";

type Props = {
    autoFocus?: boolean;
    caption?: React.ReactNode;
    defaultValue?: string;
    disabled?: boolean;
    error?: string | null;
    // hasError trumps whether or not the error field is defined.
    // i.e. hasError = defined and falsey, error="error" will NOT show an
    // error.
    hasError?: any;
    // A unique ID identifying this labeled field. A random number will be
    // generated if this isn't provided.
    id?: string;
    name?: string;
    onBlur?: (e: React.FocusEvent) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onKeyPress?: (e: React.KeyboardEvent) => void;
    pattern?: string;
    placeholder?: string;
    showTitle: boolean;
    testId?: string;
    title: string;
    // Extra styles used to style the title.
    titleStyle?: any;
    transparent: boolean;
    type: FieldType;
    value?: string;
    warning?: string;
};

type DefaultProps = {
    showTitle: Props["showTitle"];
    transparent: Props["transparent"];
    type: Props["type"];
};

/**
 * @deprecated Please use LabeledTextField from @khanacademy/wonder-blocks-form instead.
 */
class DeprecatedLabeledField extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        type: "text",
        showTitle: true,
        transparent: false,
    };

    constructor(props: Props) {
        super(props);
        this.id = "kui_labeled_field_" + (props.id || uniqueIdCounter++);
        this.input = null;
    }

    id: string;
    input: HTMLInputElement | null;

    getInputElem(): HTMLInputElement | null {
        return this.input;
    }

    handleTitleClick() {
        this.input?.focus();
    }

    render(): React.ReactNode {
        const hasWarning = this.props.warning != null;
        const hasError =
            this.props.hasError === undefined
                ? this.props.error
                : this.props.hasError;

        let label = null;
        if (hasError) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            label = (
                <div id={this.id + "-error"} className={css(styles.error)}>
                    {this.props.error}
                </div>
            );
        } else if (!this.props.showTitle) {
            // If we have no title and there is no error message, display a
            // screen-reader-only label with the placeholder text
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            label = (
                <div id={this.id} className="sr-only">
                    {this.props.placeholder}
                </div>
            );
        }

        return (
            <div className={`kui-labeledfield ${css(styles.base)}`}>
                {this.props.showTitle && (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
                    <div
                        className={css(styles.title)}
                        onClick={() => this.handleTitleClick()}
                        id={this.id}
                        style={this.props.titleStyle}
                    >
                        {this.props.title}
                        {this.props.warning && (
                            <span title={this.props.warning}>
                                <DeprecatedIcon
                                    color="#ffCC00"
                                    className={css(styles.warningIcon)}
                                    icon={warningSign}
                                />
                            </span>
                        )}
                    </div>
                )}
                {label}
                <input
                    ref={(input) => (this.input = input)}
                    aria-invalid={!!this.props.error}
                    aria-labelledby={
                        this.id + (this.props.error ? "-error" : "")
                    }
                    autoFocus={this.props.autoFocus}
                    className={css(
                        styles.input,
                        hasWarning && styles.inputWarning,
                        hasError && styles.inputError,
                        this.props.transparent && styles.inputTransparent,
                    )}
                    data-testid={this.props.testId}
                    defaultValue={this.props.defaultValue}
                    disabled={this.props.disabled}
                    name={this.props.name}
                    placeholder={this.props.placeholder}
                    pattern={this.props.pattern}
                    type={this.props.type}
                    value={this.props.value}
                    onBlur={this.props.onBlur}
                    onFocus={this.props.onFocus}
                    onChange={this.props.onChange}
                    onClick={this.props.onClick}
                    onKeyPress={this.props.onKeyPress}
                />
                {this.props.caption && (
                    <div className={css(styles.caption)}>
                        {this.props.caption}
                    </div>
                )}
            </div>
        );
    }
}

const errorColor = "#c52717";

const styles = StyleSheet.create({
    base: {
        lineHeight: 1.4,
        marginBottom: 20,
    },

    title: {
        color: "#777",
        cursor: "default",
        float: "left",
        fontSize: 14,
        fontWeight: "bold",
        textTransform: "uppercase",
    },

    error: {
        color: errorColor,
        float: "right",
        marginLeft: 20,
        textAlign: "right",
    },

    input: {
        background: "#fcfcfc",
        border: `1px solid ${color.offBlack50}`,
        boxSizing: "border-box",
        clear: "both",
        font: "inherit",
        padding: "8px 10px",
        margin: "5px 0 0",
        verticalAlign: "baseline",
        width: "100%",

        ":focus": {
            outline: "none",
            boxShadow: "0px 0px 10px rgba(99, 155, 36, 0.8)",
        },
    },

    inputError: {
        background: "#fbe2e0",
        borderColor: errorColor,

        ":focus": {
            boxShadow: "0px 0px 10px rgba(197, 39, 23, 0.4)",
        },
    },

    inputWarning: {
        background: "#ffEECC",
        borderColor: "#ffCC00",
    },

    inputTransparent: {
        background: "transparent",
        color: "#fff",
    },

    caption: {
        color: "#999",
        marginTop: 5,
        textAlign: "right",
    },

    warningIcon: {
        marginLeft: 4,
        position: "relative",
        top: 1,
    },
});

export default DeprecatedLabeledField;
